import { DocumentDuplicateIcon, PlusIcon, SparklesIcon, TrashIcon } from "@heroicons/react/24/outline";
import { Button, Input, RowAction, Select, SectionTitle, Switch, Textarea } from "../ui";
import { COMMON, FILE_TYPES, MB, SIZE_OPTIONS, codeOf, newDocument } from "../scholarships/DocumentsTab";
import { CheckboxList } from "../scholarships/EligibilityTab";
import { criteriaRowsOf, standardRubricFor } from "../scholarships/CriteriaTab";
import { useEducationLevels, useFieldsOfStudy } from "../scholarships/model";

// The API's limits per offered programme
export const MAX_DOCUMENTS = 20;
export const MAX_CRITERIA = 10;

const sizeOptionsOf = (maxBytes) =>
  SIZE_OPTIONS.some((o) => o.value === String(maxBytes)) ? SIZE_OPTIONS : [{ value: String(maxBytes), label: `${(maxBytes / MB).toFixed(1)} MB` }, ...SIZE_OPTIONS];

// The documents applicants upload for one programme; `errors` keyed "<index>.<field>"
export function RequirementsSection({ documents, onChange, errors = {}, disabled }) {
  const update = (index, field, value) => onChange(documents.map((d, i) => (i === index ? { ...d, [field]: value } : d)));
  const toggleType = (index, type) => {
    const current = documents[index].allowedTypes;
    update(index, "allowedTypes", current.includes(type) ? current.filter((t) => t !== type) : [...current, type]);
  };
  const add = (label) => documents.length < MAX_DOCUMENTS && onChange([...documents, newDocument(label)]);

  return (
    <section className="flex flex-col gap-3">
      <SectionTitle>Required documents ({documents.length})</SectionTitle>
      {documents.length === 0 && <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">No documents yet for this programme.</p>}

      {documents.map((d, i) => (
        <fieldset key={i} disabled={disabled} className="grid grid-cols-1 gap-4 rounded-lg border border-slate-100 p-4 md:grid-cols-12">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-500 md:col-span-12">
            <span>Document {i + 1}</span>
            {!disabled && <RowAction Icon={TrashIcon} label={`Remove ${d.label || "this document"}`} onClick={() => onChange(documents.filter((_, j) => j !== i))} className="ml-auto" />}
          </div>
          <Input label="Document name" required maxLength={200} value={d.label} onChange={(e) => update(i, "label", e.target.value)} error={errors[`${i}.label`]} className="md:col-span-5" />
          <Select label="Maximum size" value={String(d.maxBytes)} onChange={(e) => update(i, "maxBytes", Number(e.target.value))} options={sizeOptionsOf(d.maxBytes)} className="md:col-span-3" />
          <div className="flex flex-col gap-2 md:col-span-4">
            <span className="text-sm font-medium text-slate-700">Required to submit</span>
            <div className="flex h-[46px] items-center gap-3">
              <Switch checked={d.mandatory} onChange={(value) => update(i, "mandatory", value)} label="Required to submit" disabled={disabled} />
              <span className="text-sm text-slate-600">{d.mandatory ? "Required" : "Optional"}</span>
            </div>
          </div>
          <div className="flex flex-col gap-2 md:col-span-12">
            <span className="text-sm font-medium text-slate-700">Accepted file types</span>
            <div className="flex flex-wrap gap-4">
              {FILE_TYPES.map((t) => (
                <label key={t.value} className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" className="h-4 w-4 accent-brand" checked={d.allowedTypes.includes(t.value)} onChange={() => toggleType(i, t.value)} />
                  {t.label}
                </label>
              ))}
            </div>
            {errors[`${i}.allowedTypes`] && <p className="text-sm text-red-600">{errors[`${i}.allowedTypes`]}</p>}
          </div>
          <Textarea label="Notes for applicants" rows={2} maxLength={1000} placeholder="e.g. Certified copy, translated into English" value={d.description} onChange={(e) => update(i, "description", e.target.value)} className="md:col-span-12" />
        </fieldset>
      ))}

      {!disabled && documents.length < MAX_DOCUMENTS && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" icon={<PlusIcon className="h-4 w-4" />} onClick={() => add("")}>
            Add document
          </Button>
          {COMMON.filter((label) => !documents.some((d) => (d.code || codeOf(d.label)) === codeOf(label))).map((label) => (
            <Button key={label} variant="ghost" size="sm" icon={<DocumentDuplicateIcon className="h-4 w-4" />} onClick={() => add(label)}>
              {label}
            </Button>
          ))}
        </div>
      )}
    </section>
  );
}

/**
 * Who may apply to the programmes of one level of study (`level`, shared by `shared` offered
 * programmes); `keep` holds the saved levels and fields, shown even once retired.
 */
export function EligibilitySection({ level, shared = 1, form, onChange, keep, errors = {}, disabled }) {
  const { data: levels = [] } = useEducationLevels({ keep: keep.levels });
  const { data: fields = [] } = useFieldsOfStudy({ keep: keep.fields });
  const update = (patch) => onChange({ ...form, ...patch });

  return (
    <section className="flex flex-col gap-4">
      <SectionTitle>Who may apply{level ? `: ${level} programmes` : ""}</SectionTitle>
      <p className="text-sm text-slate-500">
        {shared > 1 ? `These rules apply to all ${shared} ${level ?? ""} programmes of this call. ` : ""}Rules of one kind are alternatives; every kind set must hold. Students who miss them
        may still apply, and staff see the application flagged.
      </p>
      <CheckboxList label="Current level of study" options={levels.map((l) => ({ value: l.id, label: l.levelName }))} selected={form.levels} onChange={(ids) => update({ levels: ids })} disabled={disabled} />
      <CheckboxList label="Field of study" options={fields.map((f) => ({ value: f.id, label: f.fieldName }))} selected={form.fields} onChange={(ids) => update({ fields: ids })} disabled={disabled} />
      <div className="grid gap-4 sm:grid-cols-2">
        <Input type="number" label="Minimum final grade" suffix="%" min={0} max={100} step="0.1" placeholder="No minimum" value={form.minGrade} onChange={(e) => update({ minGrade: e.target.value })} error={errors.minGrade} disabled={disabled} />
      </div>
    </section>
  );
}

export const totalWeightOf = (rows) => rows.reduce((sum, row) => sum + (Number(row.weight) || 0), 0);

// How evaluators score one programme: weights add up to 100 before the call is published; bands are kept as saved
export function CriteriaSection({ rows, level, onChange, errors = {}, disabled }) {
  const total = totalWeightOf(rows);
  const update = (index, patch) => onChange(rows.map((row, i) => (i === index ? { ...row, ...patch } : row)));

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <SectionTitle>Marking criteria</SectionTitle>
        <span className={`rounded-md px-2 py-1 text-sm font-semibold ${total === 100 ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>Weights: {total} / 100</span>
      </div>
      {errors.total && <p className="text-sm text-red-600">{errors.total}</p>}

      {rows.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-slate-200 p-8 text-center">
          <p className="text-sm text-slate-600">No criteria yet.</p>
          {!disabled && (
            <Button variant="outline" icon={<SparklesIcon className="h-4 w-4" />} onClick={() => onChange(criteriaRowsOf(standardRubricFor(level)))}>
              Start from HEC's standard criteria
            </Button>
          )}
        </div>
      ) : (
        <ol className="flex flex-col gap-3">
          {rows.map((row, i) => (
            <li key={i} className="grid gap-3 rounded-lg border border-slate-200 p-4 md:grid-cols-[1fr_8rem_auto]">
              <Input label={`Criterion ${i + 1}`} required maxLength={200} value={row.name} onChange={(e) => update(i, { name: e.target.value })} error={errors[`${i}.name`]} disabled={disabled} />
              <Input type="number" label="Weight" required min={1} max={100} suffix="%" value={row.weight} onChange={(e) => update(i, { weight: e.target.value })} error={errors[`${i}.weight`]} disabled={disabled} />
              <div className="flex items-end">{!disabled && <RowAction Icon={TrashIcon} label="Remove" variant="ghost" onClick={() => onChange(rows.filter((_, j) => j !== i))} />}</div>
              <Textarea className="md:col-span-3" label="Guidance for evaluators" rows={2} maxLength={2000} value={row.guideline} onChange={(e) => update(i, { guideline: e.target.value })} disabled={disabled} />
            </li>
          ))}
        </ol>
      )}

      {!disabled && rows.length > 0 && rows.length < MAX_CRITERIA && (
        <Button variant="outline" className="self-start" icon={<PlusIcon className="h-4 w-4" />} onClick={() => onChange([...rows, { name: "", weight: "", guideline: "", bands: [] }])}>
          Add criterion
        </Button>
      )}
    </section>
  );
}
